import type { Input } from "../shared/types.js";
import { settings, bound } from "./settings.js";
export class Controls {
  keys = new Set<string>();
  yaw = 0;
  pitch = 0;
  locked = false;
  mode: "menu" | "armed" | "gameplay" = "menu";
  onMenu = () => {};
  onLock = () => {};
  onReady = (_message: string) => {};
  onRestart = () => {};
  onInspect = () => {};
  onDraw = () => {};
  onSlash = () => {};
  private generation = 0;
  private reset = false;
  private unstick = false;
  private jumpPressed = false;
  private wheelJump = false;
  constructor(private canvas: HTMLCanvasElement) {
    document.addEventListener("pointerlockchange", () => {
      this.locked = document.pointerLockElement === canvas;
      if (this.locked && this.mode !== "menu") {
        this.mode = "gameplay";
        this.clear();
        this.onLock();
      } else if (this.locked) document.exitPointerLock();
      else if (this.mode === "gameplay") this.release();
    });
    document.addEventListener("mousemove", (e) => {
      if (this.mode !== "gameplay" || !this.locked) return;
      this.yaw -= e.movementX * 0.002 * settings.sensitivity;
      this.yaw =
        ((((this.yaw + Math.PI) % (2 * Math.PI)) + 2 * Math.PI) %
          (2 * Math.PI)) -
        Math.PI;
      this.pitch = Math.max(
        -1.55,
        Math.min(1.55, this.pitch - e.movementY * 0.002 * settings.sensitivity),
      );
    });
    document.addEventListener("keydown", (e) => {
      if (this.mode === "menu") return;
      if (e.code === "Escape" || bound("pause", e.code)) {
        e.preventDefault();
        this.release();
        return;
      }
      if (this.mode !== "gameplay" || !this.locked) return;
      if (["Space", "Tab"].includes(e.code)) e.preventDefault();
      this.keys.add(e.code);
      if (e.repeat) return;
      if (bound("restart", e.code)) this.onRestart();
      if (bound("stage", e.code)) this.reset = true;
      if (bound("unstick", e.code)) this.unstick = true;
      if (bound("inspect", e.code)) this.onInspect();
      if (bound("draw", e.code)) this.onDraw();
    });
    // Capture is requested only by a deliberate click on the gameplay viewport.
    canvas.addEventListener("click", () => {
      if (this.mode === "armed") void this.capture();
    });
    canvas.addEventListener("mousedown", (e) => {
      if (e.button === 0 && this.mode === "gameplay" && this.locked)
        this.onSlash();
    });
    document.addEventListener("keyup", (e) => this.keys.delete(e.code));
    window.addEventListener("blur", () => {
      if (this.mode !== "menu") this.release();
    });
    document.addEventListener("visibilitychange", () => {
      if (document.hidden && this.mode !== "menu") this.release();
    });
    canvas.addEventListener(
      "wheel",
      (e) => {
        if (this.mode === "gameplay" && this.locked) {
          e.preventDefault();
          this.wheelJump = true;
        }
      },
      { passive: false },
    );
  }
  private clear() {
    this.keys.clear();
    this.reset = false;
    this.unstick = false;
    this.jumpPressed = false;
    this.wheelJump = false;
  }
  arm() {
    this.generation++;
    this.clear();
    this.mode = "armed";
    document.body.dataset.pointerMode = "armed";
    this.onReady("CLICK TO PLAY · ESC TO PAUSE");
  }
  async lock() {
    this.arm();
  }
  resume() {
    this.arm();
    void this.capture();
  }
  private async capture() {
    const generation = this.generation;
    try {
      try {
        await (
          this.canvas.requestPointerLock as (options?: {
            unadjustedMovement?: boolean;
          }) => Promise<void>
        )({ unadjustedMovement: settings.rawInput });
      } catch (error) {
        if (generation !== this.generation || this.mode === "menu") return;
        if (!settings.rawInput) throw error;
        await this.canvas.requestPointerLock();
      }
      if (generation !== this.generation || this.mode === "menu") {
        if (document.pointerLockElement === this.canvas)
          document.exitPointerLock();
        return;
      }
      if (document.pointerLockElement !== this.canvas)
        throw Error("Mouse capture unavailable");
      this.locked = true;
      this.mode = "gameplay";
      document.body.dataset.pointerMode = "gameplay";
      this.onLock();
    } catch (error) {
      console.warn("Mouse capture unavailable", error);
      if (generation === this.generation && this.mode !== "menu") {
        this.mode = "armed";
        this.locked = false;
        this.clear();
        this.onReady("MOUSE CAPTURE UNAVAILABLE · ESC TO PAUSE");
      }
    }
  }
  release(notify = true) {
    this.generation++;
    this.mode = "menu";
    this.locked = false;
    this.clear();
    document.body.dataset.pointerMode = "menu";
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
    if (notify) this.onMenu();
  }
  sample(): Input {
    const active = this.mode === "gameplay" && this.locked;
    const down = (name: string) =>
      active && settings.binds[name]?.some((key) => this.keys.has(key));
    const held = down("jump"),
      jump = settings.autoBhop
        ? held || this.wheelJump
        : (held && !this.jumpPressed) || this.wheelJump;
    this.jumpPressed = held;
    this.wheelJump = false;
    const input = {
      f: Number(down("forward")) - Number(down("back")),
      s: Number(down("left")) - Number(down("right")),
      jump: active && jump,
      duck: down("duck"),
      yaw: this.yaw,
      pitch: this.pitch,
      reset: active && this.reset,
      unstick: active && this.unstick,
    };
    this.reset = false;
    this.unstick = false;
    return input;
  }
}
